import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { showSuccess, showError } from '../utils/toastUtils';
import { fetchAPI } from '../utils/api';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaSchool, FaUserFriends, FaCity, FaCalendarAlt, FaSpinner, FaLock } from 'react-icons/fa';

export default function StudentForm() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        // Account
        name: '',
        email: '',
        password: '',
        role: 'student',
        student_type: 'physical',

        // Personal
        phone: '',
        birthday: '',
        age: '',
        address: '',
        district: '',
        city: '',

        // Academic
        school_name: '',
        institution: '',
        medium: 'English', // Default to Sinhala
        stream: '',
        grade: '',
        year: new Date().getFullYear().toString(),

        // Guardian
        guardian_name: '',
        guardian_contact: ''

    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            // Auto-set password to phone number when phone changes
            ...(name === 'phone' ? { password: value } : {})
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);



        try {
            await fetchAPI('/admin/add_user.php', {
                method: 'POST',
                body: JSON.stringify(formData)
            });
            showSuccess('Student registered successfully!');
            navigate('/'); // Redirect to home page
        } catch (err) {
            showError(err.message || 'Failed to register student');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-10">
                    <h2 className="text-3xl font-extrabold text-gray-900">Student Registration</h2>
                    <p className="mt-2 text-sm text-gray-600">Create a new student account with full academic profile.</p>
                </div>

                <form onSubmit={handleSubmit} className="bg-white shadow-xl rounded-2xl overflow-hidden">
                    <div className="p-8 space-y-8">

                        {/* 1. Account Details Section */}
                        <div className="space-y-6">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 border-b pb-2">
                                <span className="bg-blue-100 text-blue-600 p-2 rounded-lg"><FaUser /></span>
                                Account Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                                    <input required name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Student's Full Name" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                                    <input required type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" placeholder="student@gmail.com" />
                                </div>


                            </div>
                        </div>

                        {/* 2. Personal Information Section */}
                        <div className="space-y-6">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 border-b pb-2">
                                <span className="bg-green-100 text-green-600 p-2 rounded-lg"><FaUserFriends /></span>
                                Personal Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Birthday</label>
                                    <input type="date" name="birthday" value={formData.birthday} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-green-500 outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                                    <input type="number" name="age" value={formData.age} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-green-500 outline-none" placeholder="18" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-green-500 outline-none" placeholder="07XXXXXXXX" required />
                                </div>
                                <div className="md:col-span-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                                    <input name="address" value={formData.address} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-green-500 outline-none" placeholder="Residential Address" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                    <input name="city" value={formData.city} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-green-500 outline-none" placeholder="City" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                                    <select name="district" value={formData.district} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-green-500 outline-none">
                                        <option value="">Select District</option>
                                        {['Colombo', 'Gampaha', 'Kalutara', 'Kandy', 'Matale', 'Nuwara Eliya', 'Galle', 'Matara', 'Hambantota', 'Jaffna', 'Kilinochchi', 'Mannar', 'Vavuniya', 'Mullaitivu', 'Batticaloa', 'Ampara', 'Trincomalee', 'Kurunegala', 'Puttalam', 'Anuradhapura', 'Polonnaruwa', 'Badulla', 'Monaragala', 'Ratnapura', 'Kegalle'].map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* 3. Guardian Information Section */}
                        <div className="space-y-6">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 border-b pb-2">
                                <span className="bg-purple-100 text-purple-600 p-2 rounded-lg"><FaUserFriends /></span>
                                Guardian Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Name</label>
                                    <input name="guardian_name" value={formData.guardian_name} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500 outline-none" placeholder="Parent/Guardian Name" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Guardian Contact</label>
                                    <input type="tel" name="guardian_contact" value={formData.guardian_contact} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-purple-500 outline-none" placeholder="Emergency Contact" />
                                </div>
                            </div>
                        </div>

                        {/* 4. Academic Information Section */}
                        <div className="space-y-6">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 border-b pb-2">
                                <span className="bg-orange-100 text-orange-600 p-2 rounded-lg"><FaSchool /></span>
                                Academic Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Student Type & Index Number (Moved here to match UserManagement style) */}
                                <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-blue-50/50 rounded-xl border border-blue-100">
                                    <div>
                                        <label className="block text-sm font-bold text-blue-800 mb-3">Student Type (Required for Index)</label>
                                        <div className="flex gap-4">
                                            <label className={`flex-1 cursor-pointer border rounded-xl p-3 flex flex-col items-center justify-center gap-1 transition-all ${formData.student_type === 'physical' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white hover:bg-gray-50'}`}>
                                                <input type="radio" name="student_type" value="physical" checked={formData.student_type === 'physical'} onChange={handleChange} className="hidden" />
                                                <span className="font-bold text-sm">Physical Class</span>
                                                <span className="text-[10px] text-gray-500">Prefix: SMP</span>
                                            </label>
                                            <label className={`flex-1 cursor-pointer border rounded-xl p-3 flex flex-col items-center justify-center gap-1 transition-all ${formData.student_type === 'online' ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-white hover:bg-gray-50'}`}>
                                                <input type="radio" name="student_type" value="online" checked={formData.student_type === 'online'} onChange={handleChange} className="hidden" />
                                                <span className="font-bold text-sm">Online Class</span>
                                                <span className="text-[10px] text-gray-500">Prefix: SMO</span>
                                            </label>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-blue-800/70 mb-1.5">Index Number</label>
                                        <div className="relative">
                                            <input
                                                disabled
                                                value="Auto-Generated after save"
                                                className="w-full pl-4 pr-10 py-3 bg-white/50 border border-blue-200 rounded-xl text-blue-900 placeholder:text-blue-300 font-mono tracking-wider cursor-not-allowed"
                                                title="Index Number is auto-generated"
                                            />
                                            <div className="absolute right-3 top-3 text-blue-400">
                                                <FaLock size={14} />
                                            </div>
                                        </div>
                                        <p className="text-[10px] text-blue-600/60 mt-1">
                                            System will assign the next available number automatically.
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
                                    <input name="school_name" value={formData.school_name} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-orange-500 outline-none" placeholder="School Name" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Institution</label>
                                    <select name="institution" value={formData.institution} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-orange-500 outline-none" required>
                                        <option value="">Select Institution</option>
                                        {['Vishwa-Nittabuwa', 'Sipsara-Gampaha', 'GoldenGate-Kiribathgoda', 'Sudesh Maths -Online'].map(inst => (
                                            <option key={inst} value={inst}>{inst}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Medium</label>
                                    <select name="medium" value={formData.medium} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-orange-500 outline-none">
                                        <option value="English">English Medium</option>
                                        <option value="Sinhala">Sinhala Medium</option>

                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Stream</label>
                                    <select name="stream" value={formData.stream} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-orange-500 outline-none">
                                        <option value="">Select Stream</option>
                                        <option value="Physical Science">O/L</option>
                                        <option value="Biological Science">A/L</option>

                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                                    <select name="grade" value={formData.grade} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-orange-500 outline-none">
                                        <option value="">Select Grade</option>
                                        <option value="6">Grade 6</option>
                                         <option value="7">Grade 7</option>                                       
                                        <option value="8">Grade 8</option>
                                        <option value="9">Grade 9</option>
                                        <option value="10">Grade 10</option>
                                        <option value="11">Grade 11</option>
                                        <option value="12">Grade 12</option>
                                        <option value="13">Grade 13</option>
                                        <option value="Private">Private</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Batch Year</label>
                                    <input name="year" value={formData.year} onChange={handleChange} className="w-full px-4 py-2 border rounded-xl focus:ring-2 focus:ring-orange-500 outline-none" placeholder="Year" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="bg-gray-50 px-8 py-6 flex justify-end gap-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="px-6 py-2.5 text-gray-700 font-bold hover:bg-gray-200 rounded-xl transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-8 py-2.5 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 shadow-lg shadow-gray-200 transition-all transform active:scale-95 flex items-center gap-2"
                        >
                            {loading ? <FaSpinner className="animate-spin" /> : 'Register Student'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
